<template>
  <nav class="omx-crumbs" aria-label="Breadcrumb" v-if="crumbs.length">
    <ol>
      <li v-for="(crumb, i) in crumbs" :key="i">
        <router-link v-if="crumb.to && i !== crumbs.length - 1" :to="crumb.to">{{ crumb.label }}</router-link>
        <span v-else class="omx-crumbs__here">{{ crumb.label }}</span>
        <span v-if="i !== crumbs.length - 1" class="omx-crumbs__sep" aria-hidden="true">/</span>
      </li>
    </ol>
  </nav>
</template>

<script>
/**
 * OmxBreadcrumbs — derives a route trail from $route.name + params.
 *
 * Phase 1 keeps this simple: a static name → label map. Dynamic IDs
 * render as short tokens (P‑1042, T‑42) so the chain stays scannable
 * without requiring data fetches.
 */
const NAME_MAP = {
  Projects:         { labelKey: 'top-menu.projects',          to: '/projects' },
  // As a parent crumb (Summary/Logs/Task/…) the id lives in `project_id`; on its
  // own page it's `id`. Accept either so the middle "Project" crumb links correctly.
  ProjectDetails:   { labelKey: 'breadcrumbs.project',        to: r => `/projects/${r.params.project_id || r.params.id}` },
  TaskDetails:      { labelKey: 'breadcrumbs.task',           to: r => `/projects/${r.params.project_id}/tasks/${r.params.task_id}` },
  PersonalDetails:  { labelKey: 'breadcrumbs.person',         to: r => `/projects/${r.params.project_id}/personnel/${r.params.personal_id}` },
  EquipmentDetails: { labelKey: 'breadcrumbs.equipment',      to: r => `/projects/${r.params.project_id}/equipments/${r.params.equipment_id}` },
  ConsumableDetails:{ labelKey: 'breadcrumbs.consumable',     to: r => `/projects/${r.params.project_id}/consumables/${r.params.consumable_id}` },
  ProjectLogs:      { labelKey: 'breadcrumbs.logs',           to: r => `/projects/${r.params.project_id}/worklogs` },
  ProjectSummary:   { labelKey: 'breadcrumbs.summary',        to: r => `/projects/${r.params.project_id}/summary` },
  Users:            { labelKey: 'top-menu.users',             to: '/users' },
  UserDetails:      { labelKey: 'breadcrumbs.user',           to: r => `/users/${r.params.id}` },
  Templates:        { labelKey: 'top-menu.project-templates', to: '/templates' },
  CategoricalData:  { labelKey: 'top-menu.categories',        to: '/categories' }
};

// Parent chains by route name (deepest first). Allows us to render
// Projects › Project P-1042 › Task T-7 even though router has no
// nested route definitions.
const PARENT_CHAIN = {
  ProjectDetails:   ['Projects'],
  TaskDetails:      ['Projects', 'ProjectDetails'],
  PersonalDetails:  ['Projects', 'ProjectDetails'],
  EquipmentDetails: ['Projects', 'ProjectDetails'],
  ConsumableDetails:['Projects', 'ProjectDetails'],
  ProjectLogs:      ['Projects', 'ProjectDetails'],
  ProjectSummary:   ['Projects', 'ProjectDetails'],
  UserDetails:      ['Users']
};

export default {
  name: "OmxBreadcrumbs",
  computed: {
    crumbs() {
      const route = this.$route;
      if (!route || !route.name || !NAME_MAP[route.name]) return [];

      const chain = (PARENT_CHAIN[route.name] || []).concat([route.name]);
      // A lone top-level crumb (Projects/Users/Templates/Categories) just repeats
      // the page title shown by OmxList — redundant, so render no breadcrumb.
      if (chain.length <= 1) return [];
      return chain.map((name, idx) => {
        const entry = NAME_MAP[name];
        if (!entry) return null;
        const to = typeof entry.to === 'function' ? entry.to(route) : entry.to;
        return {
          label: this.labelFor(name, route, idx === chain.length - 1),
          to
        };
      }).filter(Boolean);
    }
  },
  methods: {
    labelFor(name, route, isLeaf) {
      const entry = NAME_MAP[name];
      const base = this.$t(entry.labelKey);

      // Leaf detail pages: append a short id token for context
      if (!isLeaf) return base;
      const id = this.idTokenFor(name, route);
      return id ? `${base} · ${id}` : base;
    },
    idTokenFor(name, route) {
      const p = route.params || {};
      switch (name) {
        case 'ProjectDetails':   return this.short(p.id);
        case 'TaskDetails':      return this.short(p.task_id);
        case 'PersonalDetails':  return this.short(p.personal_id);
        case 'EquipmentDetails': return this.short(p.equipment_id);
        case 'ConsumableDetails':return this.short(p.consumable_id);
        case 'UserDetails':      return this.short(p.id);
        default: return null;
      }
    },
    short(id) {
      if (!id) return null;
      if (id.length <= 8) return id;
      return id.slice(0, 6) + '…';
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.omx-crumbs {
  font-size: 13px;
  color: $omx-ink-500;
  min-width: 0;

  ol {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
  }
  li {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
  }
  a {
    color: $omx-ink-500;
    text-decoration: none;
    padding: 2px 4px;
    border-radius: 4px;
    &:hover { color: $omx-brand-600; background: $omx-brand-50; text-decoration: none; }
  }
  &__sep {
    color: $omx-ink-300;
    padding: 0 2px;
  }
  &__here {
    color: $omx-ink-900;
    font-weight: 600;
    padding: 2px 4px;
  }
}
</style>
